import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import CapabilityErrorProvider from './components/CapabilityErrorProvider';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientesPage from './pages/ClientesPage';
import PresupuestosPage from './pages/PresupuestosPage';
import ObrasPage from './pages/ObrasPage';
import GastosPage from './pages/GastosPage';
import FacturasPage from './pages/FacturasPage';
import PagosPage from './pages/PagosPage';
import IncidenciasPage from './pages/IncidenciasPage';
import PendientesPage from './pages/PendientesPage';
import CalculadoraROI from './pages/CalculadoraROI';
import ConfiguracionDocumentos from './pages/ConfiguracionDocumentos';
import ConfiguracionEmpresa from './pages/ConfiguracionEmpresa';
import CalendarioObras from './pages/CalendarioObras';
import CentroAyuda from './pages/CentroAyuda';
import GestionUsuarios from './pages/GestionUsuarios';
import ActivarPage from './pages/ActivarPage';
import SetPasswordPage from './pages/SetPasswordPage';
import SuperadminDashboard from './pages/SuperadminDashboard';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Route that requires superadmin role
const SuperadminRoute = ({ children }) => {
  const { user, loading, isSuperadmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperadmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/set-password"
        element={<SetPasswordPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <ClientesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/presupuestos"
        element={
          <ProtectedRoute>
            <PresupuestosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/obras"
        element={
          <ProtectedRoute>
            <ObrasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gastos"
        element={
          <ProtectedRoute>
            <GastosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facturas"
        element={
          <ProtectedRoute>
            <FacturasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pagos"
        element={
          <ProtectedRoute>
            <PagosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/incidencias"
        element={
          <ProtectedRoute>
            <IncidenciasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pendientes"
        element={
          <ProtectedRoute>
            <PendientesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calculadora"
        element={
          <ProtectedRoute>
            <CalculadoraROI />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion-documentos"
        element={
          <ProtectedRoute>
            <ConfiguracionDocumentos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion-empresa"
        element={
          <ProtectedRoute>
            <ConfiguracionEmpresa />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendario"
        element={
          <ProtectedRoute>
            <CalendarioObras />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ayuda"
        element={
          <ProtectedRoute>
            <CentroAyuda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gestion-usuarios"
        element={
          <ProtectedRoute>
            <GestionUsuarios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activar"
        element={
          <ProtectedRoute>
            <ActivarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <SuperadminRoute>
            <SuperadminDashboard />
          </SuperadminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CapabilityErrorProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </CapabilityErrorProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
